import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { inputClassName } from './FormField';
import { AppTooltip } from './AppTooltip';

const getPageItems = (currentPage, totalPages) => {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);

  const pages = new Set([1, totalPages]);
  for (let page = Math.max(2, currentPage - 2); page <= Math.min(totalPages - 1, currentPage + 2); page += 1) {
    pages.add(page);
  }

  const ordered = [...pages].sort((a, b) => a - b);
  const items = [];
  ordered.forEach((page, index) => {
    if (index > 0 && page - ordered[index - 1] > 1) items.push(`ellipsis-${page}`);
    items.push(page);
  });
  return items;
};

export const Pagination = ({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  loading = false,
  onPageChange,
  onPageSizeChange,
  t,
}) => {
  const safeTotalPages = Math.max(1, totalPages || 1);
  const allRows = pageSize === 'all';
  const numericPageSize = allRows ? totalCount : Number(pageSize);
  const firstResult = totalCount === 0 ? 0 : (currentPage - 1) * numericPageSize + 1;
  const lastResult = allRows ? totalCount : Math.min(currentPage * numericPageSize, totalCount);

  return (
    <nav className='mt-5 flex flex-col gap-4 border-t border-[var(--border-subtle)] pt-4 lg:flex-row lg:items-center lg:justify-between' aria-label={t('Pagination')}>
      <p className='text-sm text-[var(--text-muted)]'>
        {t('Showing')} <span className='font-semibold text-[var(--text-primary)]'>{firstResult}-{lastResult}</span> {t('of')} {totalCount} {t('results')}
      </p>

      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between lg:justify-end'>
        <div className='flex items-center gap-1'>
          <AppTooltip label={t('Previous')}><button
            type='button'
            className='flex h-9 w-9 items-center justify-center rounded-[var(--radius-md)] border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:bg-[var(--surface-panel-muted)] disabled:opacity-40'
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1 || allRows}
            aria-label={t('Previous')}
          >
            <FiChevronLeft aria-hidden='true' />
          </button></AppTooltip>

          {getPageItems(currentPage, safeTotalPages).map((item) =>
            typeof item === 'string' ? (
              <span key={item} className='flex h-9 min-w-7 items-center justify-center text-[var(--text-muted)]'>...</span>
            ) : (
              <button
                key={item}
                type='button'
                className={`h-9 min-w-9 rounded-[var(--radius-md)] px-2 text-sm font-semibold ${
                  item === currentPage
                    ? 'bg-[var(--brand)] text-white shadow-sm'
                    : 'text-[var(--text-secondary)] hover:bg-[var(--surface-panel-muted)]'
                }`}
                onClick={() => onPageChange(item)}
                aria-current={item === currentPage ? 'page' : undefined}
                aria-label={`${t('Page')} ${item}`}
              >
                {item}
              </button>
            ),
          )}

          <AppTooltip label={t('Next')}><button
            type='button'
            className='flex h-9 w-9 items-center justify-center rounded-[var(--radius-md)] border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:bg-[var(--surface-panel-muted)] disabled:opacity-40'
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= safeTotalPages || allRows}
            aria-label={t('Next')}
          >
            <FiChevronRight aria-hidden='true' />
          </button></AppTooltip>
        </div>

        <label className='flex items-center gap-2 text-sm font-medium text-[var(--text-secondary)]'>
          <span>{t('Records per Page')}</span>
          <select
            className={`${inputClassName} h-10 min-w-[96px] py-1`}
            value={pageSize}
            disabled={loading}
            onChange={(event) => {
              onPageSizeChange(event.target.value === 'all' ? 'all' : Number(event.target.value));
              event.currentTarget.blur();
            }}
          >
            {[10, 15, 30, 50, 100, 200, 500].map((size) => <option key={size} value={size}>{size}</option>)}
            <option value='all'>{t('All')}</option>
          </select>
        </label>
      </div>
    </nav>
  );
};

export { getPageItems };
